/**
 * API Client - Centralized HTTP client for the web app.
 *
 * In development, Vite proxies /api to the Cloudflare Worker (port 8787).
 * In production, set VITE_API_BASE to the API Worker URL (e.g.
 * https://adgf-api-worker.fys2388.workers.dev/api/v1).
 */

const API_BASE = (import.meta.env.VITE_API_BASE as string) || '/api/v1';

export interface ApiError {
  error: string;
  details?: string;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(body.error || `API request failed: ${response.status}`);
  }

  return body as T;
}

// Games
export interface TransitionOptions {
  /** Set true to explicitly approve a human-only transition (APPROVED/PUBLISHED). */
  humanApproved?: boolean;
  /** Human reviewer id required when humanApproved is true. */
  approverId?: string;
}

export const gamesApi = {
  list: (status?: string) =>
    request<{ games: unknown[]; count: number }>(status ? `/games?status=${status}` : '/games'),
  getById: (id: string) => request<{ game: unknown }>(`/games/${id}`),
  getBySlug: (slug: string) => request<{ game: unknown }>(`/games/slug/${slug}`),
  create: (data: unknown) =>
    request<{ game: unknown }>('/games', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: unknown) =>
    request<{ game: unknown }>(`/games/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  transition: (id: string, to: string, opts: TransitionOptions = {}) =>
    request<{ game: unknown }>(`/games/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify({
        to,
        ...(opts.humanApproved ? { human_approved: true } : {}),
        ...(opts.approverId ? { approver_id: opts.approverId } : {}),
      }),
    }),
};

// Game versions & rollback
export const versionsApi = {
  list: (gameId: string) =>
    request<{ versions: unknown[]; count: number; pin: unknown }>(`/games/${gameId}/versions`),
  latest: (gameId: string) => request<{ version: unknown }>(`/games/${gameId}/versions/latest`),
  rollback: (gameId: string, targetVersion: string, reason?: string) =>
    request<{ game: unknown; rollback: unknown; version: unknown }>(
      `/games/${gameId}/rollback`,
      {
        method: 'POST',
        body: JSON.stringify({ target_version: targetVersion, reason }),
      },
    ),
};

// Creator (AI game generation)
export const creatorApi = {
  generate: (spec: unknown) =>
    request<{ artifact: unknown; game_id: string; version: string }>('/creator/generate', {
      method: 'POST',
      body: JSON.stringify(spec),
    }),
  repair: (artifact: unknown, errors: { build?: string[]; qa?: string[] }) =>
    request<{ artifact: unknown }>('/creator/repair', {
      method: 'POST',
      body: JSON.stringify({ artifact, errors }),
    }),
  getStatus: () =>
    request<{
      provider: string;
      model: string;
      max_requests_per_game: number;
      prompt_version: string;
    }>('/creator/status'),
};

// Sessions
export const sessionsApi = {
  create: (data: { game_id: string; game_version: string; anonymous_user_id: string }) =>
    request<{ session: unknown }>('/sessions', { method: 'POST', body: JSON.stringify(data) }),
  end: (id: string, data: { score?: number; completed?: boolean; metadata?: Record<string, unknown> }) =>
    request<{ session: unknown }>(`/sessions/${id}/end`, { method: 'POST', body: JSON.stringify(data) }),
  get: (id: string) => request<{ session: unknown }>(`/sessions/${id}`),
};

// Analytics
export const analyticsApi = {
  getGameAnalytics: (gameId: string) =>
    request<Record<string, unknown>>(`/analytics/games/${gameId}`),
};

// Events
export const eventsApi = {
  track: (data: unknown) =>
    request<{ event: unknown }>('/events', { method: 'POST', body: JSON.stringify(data) }),
  trackBatch: (events: unknown[]) =>
    request<{ events: unknown[]; count: number }>('/events/batch', {
      method: 'POST',
      body: JSON.stringify({ events }),
    }),
};

// Daily Factory (M3.1 async production pipeline)
export interface FactoryCandidateIdea {
  title: string;
  template: string;
  runtime: string;
  theme: string;
  score: number;
  score_breakdown?: Record<string, number>;
  rank: number;
  expected_retention?: number;
}

export type FactoryStepStatus = 'PASS' | 'FAIL' | 'SKIPPED';

export interface FactoryStep {
  step: string;
  status: FactoryStepStatus;
  duration_ms: number;
  errors?: string[];
}

export interface FactoryDailyRunResponse {
  run_id: string;
  status: 'COMPLETED' | 'FAILED';
  selected_idea?: FactoryCandidateIdea;
  /** Top candidate ideas as produced by the hunter/scoring stage. */
  candidates?: FactoryCandidateIdea[];
  game_id?: string;
  game_version?: string;
  final_status: 'TESTING' | 'FAILED';
  steps: FactoryStep[];
  total_duration_ms: number;
  total_llm_tokens: number;
  error?: string;
}

// M3.1: Async Build Job types
export type BuildJobStatusType =
  | 'QUEUED'
  | 'BUILDING'
  | 'TESTING'
  | 'REPAIRING'
  | 'READY_FOR_APPROVAL'
  | 'BUILD_FAILED'
  | 'QA_FAILED'
  | 'REJECTED'
  | 'APPROVED'
  | 'PUBLISHED';

export interface BuildJobResponse {
  job_id: string;
  type: string;
  game_id?: string;
  version?: string;
  status: BuildJobStatusType;
  attempt: number;
  max_attempts: number;
  llm_requests: number;
  llm_tokens: number;
  repair_count: number;
  error_code?: string;
  error_message?: string;
  artifact_url?: string;
  checksum?: string;
  candidate_count?: number;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  elapsed_ms?: number;
}

export interface CreateJobResponse {
  job_id: string;
  status: BuildJobStatusType;
  type: string;
  candidate_count: number;
  created_at: string;
  message: string;
}

export interface JobListResponse {
  jobs: BuildJobResponse[];
  count: number;
}

export const factoryApi = {
  /** M3.1: Create an async daily factory run job (returns immediately). */
  runDaily: (candidateCount: number) =>
    request<CreateJobResponse>('/factory/daily-run', {
      method: 'POST',
      body: JSON.stringify({ candidate_count: candidateCount }),
    }),

  /** M3.1: Get a build job's current status. */
  getJob: (jobId: string) =>
    request<BuildJobResponse>(`/factory/jobs/${jobId}`),

  /** M3.1: List recent build jobs. */
  listJobs: (status?: BuildJobStatusType, limit = 20) =>
    request<JobListResponse>(
      `/factory/jobs?limit=${limit}${status ? `&status=${status}` : ''}`,
    ),

  /** M3.1: Retry a failed build job. */
  retryJob: (jobId: string) =>
    request<BuildJobResponse>(`/factory/jobs/${jobId}/retry`, { method: 'POST' }),

  /** M3.1: Human approve a job that is READY_FOR_APPROVAL. */
  approveJob: (jobId: string) =>
    request<BuildJobResponse>(`/factory/jobs/${jobId}/approve`, { method: 'POST' }),
};
